/**
 * ActivityFeed — collapsible "Recent activity" card at the top of the
 * Industry Library page. Surfaces who tweaked which industry profile
 * in the last N days so super admins can scan changes without grepping
 * API logs.
 *
 * Pairs naturally with the Health Score's *recency* dimension: profiles
 * with no activity in 30+ days lose recency points and slide down the
 * list, while this feed shows the inverse — what *did* move recently.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authedFetch from '../../lib/authedFetch';
import { toast } from 'sonner';
import {
  History, ChevronDown, ChevronUp, RefreshCw, Loader2,
  Plus, Pencil, Archive, ArchiveRestore, Trash2, Users, MoveRight,
} from 'lucide-react';
import { API } from './helpers';

/**
 * Compact "12m ago" / "2d ago" formatter. Falls back to a localised
 * date string for anything older than a week so the relative phrasing
 * doesn't get absurd ("47d ago").
 */
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Action → visual treatment. Single map keeps the row chip + icon in
// sync across every entry and is the only place to edit if we add new
// action types server-side.
const ACTION_META = {
  created: {
    icon: Plus,
    label: 'Created',
    chip: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  updated: {
    icon: Pencil,
    label: 'Updated',
    chip: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    dot: 'bg-indigo-500',
  },
  archived: {
    icon: Archive,
    label: 'Archived',
    chip: 'bg-amber-50 border-amber-200 text-amber-700',
    dot: 'bg-amber-500',
  },
  restored: {
    icon: ArchiveRestore,
    label: 'Restored',
    chip: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    dot: 'bg-emerald-500',
  },
  hard_deleted: {
    icon: Trash2,
    label: 'Deleted',
    chip: 'bg-rose-50 border-rose-200 text-rose-700',
    dot: 'bg-rose-500',
  },
  org_reassigned_in: {
    icon: MoveRight,
    label: 'Org reassigned',
    chip: 'bg-sky-50 border-sky-200 text-sky-700',
    dot: 'bg-sky-500',
  },
  org_cleared: {
    icon: Users,
    label: 'Org unset',
    chip: 'bg-slate-50 border-slate-200 text-slate-600',
    dot: 'bg-slate-400',
  },
};

function actorInitials(email) {
  if (!email) return '·';
  const local = String(email).split('@')[0] || '';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length === 0) return local.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function ActivityFeed({ onJumpToIndustry, refreshKey = 0 }) {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState(() => new Set()); // ids with diff expanded

  const load = useCallback(async (windowDays) => {
    setRefreshing(true);
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/activity?days=${windowDays}&limit=50`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setRows(data?.activity || []);
    } catch (e) {
      toast.error(`Activity feed failed to load: ${e.message || e}`);
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(days); }, [load, days, refreshKey]);

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Group counters for the header chip strip — useful at-a-glance read
  // before admins dive into the row-by-row list.
  const counters = useMemo(() => {
    const c = { total: rows.length, created: 0, updated: 0, archived: 0, reassigns: 0, actors: new Set() };
    for (const r of rows) {
      if (r.actor_email) c.actors.add(r.actor_email);
      if (r.action === 'created') c.created += 1;
      else if (r.action === 'updated') c.updated += 1;
      else if (r.action === 'archived' || r.action === 'hard_deleted') c.archived += 1;
      else if (r.action === 'org_reassigned_in' || r.action === 'org_cleared') c.reassigns += 1;
    }
    return { ...c, actorsCount: c.actors.size };
  }, [rows]);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid="industry-activity-feed"
    >
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-slate-100">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 text-left min-w-0 flex-1"
          data-testid="industry-activity-toggle"
        >
          <div className="rounded-lg bg-indigo-50 text-indigo-600 p-1.5 shrink-0">
            <History className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Recent activity
              <span className="text-[10px] font-mono text-slate-400">last {days}d</span>
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {loading
                ? 'Loading…'
                : counters.total === 0
                  ? 'No mutations yet — every create / edit / archive / org reassign shows up here.'
                  : (
                    <>
                      <span className="font-mono">{counters.total}</span> event{counters.total === 1 ? '' : 's'}
                      {' '}· <span className="font-mono">{counters.actorsCount}</span> admin{counters.actorsCount === 1 ? '' : 's'}
                      {counters.updated ? <> · <span className="font-mono">{counters.updated}</span> edits</> : null}
                      {counters.created ? <> · <span className="font-mono">{counters.created}</span> created</> : null}
                      {counters.reassigns ? <> · <span className="font-mono">{counters.reassigns}</span> org moves</> : null}
                    </>
                  )}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {open && (
            <>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="h-7 rounded-md border border-slate-200 bg-white px-2 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500"
                data-testid="industry-activity-window"
                title="Lookback window"
              >
                <option value={1}>1d</option>
                <option value={7}>7d</option>
                <option value={14}>14d</option>
                <option value={30}>30d</option>
                <option value={90}>90d</option>
              </select>
              <button
                type="button"
                onClick={() => load(days)}
                disabled={refreshing}
                className="h-7 w-7 rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center disabled:opacity-50"
                data-testid="industry-activity-refresh"
                title="Refresh"
              >
                {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="h-7 w-7 rounded-md text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center"
            data-testid="industry-activity-collapse"
            aria-label={open ? 'Collapse activity feed' : 'Expand activity feed'}
          >
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 py-3" data-testid="industry-activity-body">
          {loading ? (
            <div className="py-6 text-center text-xs text-slate-500">
              <Loader2 className="inline h-3.5 w-3.5 animate-spin mr-1.5" /> Loading recent activity…
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-xs text-slate-500" data-testid="industry-activity-empty">
              No mutations in the last {days}d. The feed populates as soon as anyone creates / edits / archives a profile or reassigns an org.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="industry-activity-list">
              {rows.map((row) => {
                const meta = ACTION_META[row.action] || ACTION_META.updated;
                const Icon = meta.icon;
                const isExpanded = expanded.has(row.id);
                const hasDiff = (row.diff || []).length > 0;
                return (
                  <li
                    key={row.id}
                    className="rounded-lg border border-slate-100 bg-slate-50/40 hover:bg-slate-50 transition-colors p-2.5"
                    data-testid={`industry-activity-row-${row.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className={`shrink-0 mt-0.5 h-6 w-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white ${meta.dot}`}
                        title={row.actor_email || 'system'}
                      >
                        {actorInitials(row.actor_email)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${meta.chip}`}>
                            <Icon className="h-2.5 w-2.5" />
                            {meta.label}
                          </span>
                          {row.industry_name && row.industry_id && (
                            <button
                              type="button"
                              onClick={() => onJumpToIndustry?.(row.industry_id)}
                              className="text-xs font-semibold text-slate-900 hover:text-indigo-700 truncate"
                              data-testid={`industry-activity-jump-${row.id}`}
                            >
                              {row.industry_name}
                            </button>
                          )}
                          {row.industry_id && !row.industry_name && (
                            <span className="text-xs font-mono text-slate-500 truncate">{row.industry_id}</span>
                          )}
                          <span className="text-[10px] text-slate-400" title={row.created_at}>
                            {relativeTime(row.created_at)}
                          </span>
                        </div>
                        {row.summary && (
                          <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">{row.summary}</p>
                        )}
                        {row.actor_email && (
                          <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">
                            by {row.actor_email}
                          </p>
                        )}
                        {hasDiff && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(row.id)}
                            className="mt-1 text-[10px] text-indigo-600 hover:underline"
                            data-testid={`industry-activity-diff-toggle-${row.id}`}
                          >
                            {isExpanded ? 'Hide diff' : `View diff (${row.diff.length} field${row.diff.length === 1 ? '' : 's'})`}
                          </button>
                        )}
                        {isExpanded && hasDiff && (
                          <div
                            className="mt-1.5 rounded-md border border-slate-200 bg-white p-2 text-[11px] space-y-1"
                            data-testid={`industry-activity-diff-${row.id}`}
                          >
                            {row.diff.map((d, i) => (
                              <div key={i} className="flex items-baseline gap-2 font-mono">
                                <span className="text-slate-500 shrink-0">{d.field}</span>
                                <span className="text-rose-600 truncate">
                                  {d.kind === 'count' ? `${d.from} rows` : String(d.from ?? '∅')}
                                </span>
                                <span className="text-slate-400">→</span>
                                <span className="text-emerald-700 truncate">
                                  {d.kind === 'count' ? `${d.to} rows` : String(d.to ?? '∅')}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
